import {
  forwardRef,
  memo,
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import FileManager, {
  Column,
  ContextMenu,
  ContextMenuItem,
  Details,
  ItemView,
  Permissions,
  Toolbar,
  ToolbarItem,
  type FileManagerRef
} from 'devextreme-react/file-manager';
import LoadIndicator from 'devextreme-react/load-indicator';
import { custom } from 'devextreme/ui/dialog';
import type FileSystemItem from 'devextreme/file_management/file_system_item';
import type {
  ContextMenuItemClickEvent,
  ContextMenuShowingEvent,
  ItemDeletingEvent,
  SelectedFileOpenedEvent,
  ToolbarItemClickEvent
} from 'devextreme/ui/file_manager';
import { listMounts, type MountSummary, type StorageMountCatalog } from './fileBrowserApi';
import {
  extraActionsForSelection,
  extraContextMenuItems,
  extraToolbarItems,
  invokeExtraFileAction,
  type FileBrowserExtraFileAction
} from './fileBrowserExtraActions';
import {
  buildMountRoutes,
  createUnifiedFileBrowserProvider,
  getDeleteInfo,
  parseVirtualPath,
  type MountRoute
} from './createFileBrowserProvider';
import { buildBatchDeleteConfirmationMessage } from './fileBrowserDeleteConfirmation';
import { customizeFileBrowserThumbnail } from './fileBrowserThumbnails';
import {
  buildFileBrowserSearchOptions,
  clearFileBrowserSearch,
  createFileBrowserSearchController,
  JARVIS_FILE_SEARCH,
  syncFileBrowserSearch
} from './fileBrowserSearch';
import { customizeFileBrowserDetailColumns } from './fileBrowserDetailColumns';
import {
  isFileBrowserDetailsView,
  JARVIS_COLUMN_CHOOSER,
  persistFileBrowserGridState,
  scheduleConfigureFileBrowserDetailsGridFromRef
} from './fileBrowserDetailsGrid';
import { FileBrowserColumnChooserPopup } from './FileBrowserColumnChooserPopup';
import { FileBrowserDestinationPickerPopup } from './FileBrowserDestinationPickerPopup';
import {
  buildDestinationDirectory,
  filterTransferableItems,
  JARVIS_COPY_TO,
  JARVIS_MOVE_TO,
  transferSelectionLabel
} from './fileBrowserDestination';
import { performFileTransfers } from './fileBrowserTransfer';
import {
  isFileBrowserViewMode,
  readFileBrowserViewModeForLayout,
  writeFileBrowserViewMode
} from './fileBrowserViewMode';
import {
  fileManagerDirectoryPath,
  persistNavigatedLocation,
  resolveLastLocation,
  toFileManagerCurrentPath,
  toFileManagerCurrentPathKeys
} from './fileBrowserLastLocation';
import { useShellLayout } from '../shell/useShellLayout';
import { useNavigate } from 'react-router';
import {
  scheduleFileSelectionToolbarSync,
  syncFileSelectionToolbar
} from './fileBrowserToolbar';
import { showToast } from '../notifications/toast';
import { MediaPreviewPopup, type MediaPreviewState } from '../mediaViewer/MediaPreviewPopup';
import { DocumentPreviewPopup, type DocumentPreviewState } from '../documentEditor/DocumentPreviewPopup';
import { PdfPreviewPopup, type PdfPreviewState } from '../pdfViewer/PdfPreviewPopup';
import {
  DOC_TOOLBAR_EDIT_EXCEL,
  DOC_TOOLBAR_EDIT_WORD,
  DOC_TOOLBAR_PREVIEW,
  handleContextMenuItemClick,
  handleContextMenuShowing,
  handleSelectedFileOpened,
  JARVIS_ZIP,
  MICROSOFT_365_OPEN,
  MEDIA_TOOLBAR_OPEN,
  MEDIA_TOOLBAR_PREVIEW,
  PDF_TOOLBAR_PREVIEW,
  openDocumentEditorTab,
  openDocumentPreview,
  openMediaPreview,
  openInMicrosoft365,
  openPdfPreview,
  openSelectedMediaViewerTab
} from './mediaBrowserActions';
import { isMountUnavailable, mountHealthMessage } from './fileBrowserMountAccess';
import {
  applyClipboardCutStyles,
  clearClipboard,
  cutOrCopySelection,
  getClipboardState,
  isClipboardShortcutTarget,
  JARVIS_CLIPBOARD_COPY,
  JARVIS_CUT,
  JARVIS_PASTE,
  pasteClipboardIntoDirectory,
  subscribeFileBrowserClipboard
} from './fileBrowserClipboard';
import { FileBrowserClipboardBanner } from './FileBrowserClipboardBanner';
import { JARVIS_SHARE, shareFileBrowserItem } from './fileBrowserShare';
import {
  buildZipRequest,
  createZipArchive,
  focusZipArchiveAfterCreate,
  normalizeArchiveName,
  promptArchiveName
} from './fileBrowserZip';
import {
  buildNewFileMenuItems,
  createAndOpenNewStarterFile,
  JARVIS_NEW_FILE
} from './fileBrowserNewFile';
import { getNewFileStarter } from './fileTypeRegistry';
import { FileBrowserNewFilePromptPopup } from './FileBrowserNewFilePromptPopup';
import './FileBrowser.css';

export type FileManagerPaneHandle = {
  resyncToolbar: () => void;
};

type FileManagerPaneProps = {
  mounts: MountSummary[];
  catalog: StorageMountCatalog;
  isNarrow: boolean;
  isPhone: boolean;
  allowOfficeEdit: boolean;
  extraFileActions?: FileBrowserExtraFileAction[];
  onOpenMediaPreview: (state: MediaPreviewState) => void;
  onOpenDocumentPreview: (state: DocumentPreviewState) => void;
  onOpenPdfPreview: (state: PdfPreviewState) => void;
};

type DeleteConfirmationBatch = {
  key: string;
  remainingItemKeys: Set<string>;
  confirmation: Promise<boolean>;
};

const UPLOAD_OPTIONS = { chunkSize: 0 } as const;

const FileManagerPane = memo(
  forwardRef<FileManagerPaneHandle, FileManagerPaneProps>(function FileManagerPane(
    { mounts, catalog, isNarrow, isPhone, allowOfficeEdit, extraFileActions, onOpenMediaPreview, onOpenDocumentPreview, onOpenPdfPreview },
    ref
  ) {
    const navigate = useNavigate();
    const [currentMount, setCurrentMount] = useState<MountRoute | null>(null);
    const [columnChooserOpen, setColumnChooserOpen] = useState(false);
    const [newFilePromptStarterId, setNewFilePromptStarterId] = useState<string | null>(null);
    const [destinationPicker, setDestinationPicker] = useState<{
      operation: 'Copy' | 'Move';
      items: FileSystemItem[];
    } | null>(null);
    const [clipboardState, setClipboardState] = useState(getClipboardState);
    const [currentDirectoryLabel, setCurrentDirectoryLabel] = useState('this folder');
    const [fileManagerCurrentPath, setFileManagerCurrentPath] = useState(() =>
      toFileManagerCurrentPath(resolveLastLocation(catalog, buildMountRoutes(mounts)))
    );
    const [fileManagerCurrentPathKeys, setFileManagerCurrentPathKeys] = useState(() =>
      toFileManagerCurrentPathKeys(resolveLastLocation(catalog, buildMountRoutes(mounts)))
    );
    const [pasteBusy, setPasteBusy] = useState(false);
    const paneRef = useRef<HTMLDivElement>(null);
    const fileManagerRef = useRef<FileManagerRef>(null);
    const viewModeSetupRef = useRef(false);
    const lastPathRestoredRef = useRef(false);
    const hasEnteredFolderRef = useRef(false);
    const narrowNavClosedRef = useRef(false);
    const canWriteRef = useRef(false);
    const allowOfficeEditRef = useRef(true);
    const routesRef = useRef<readonly MountRoute[]>([]);
    const currentDirectoryPathRef = useRef('');
    const deleteConfirmationBatchRef = useRef<DeleteConfirmationBatch | null>(null);
    const onOpenMediaPreviewRef = useRef(onOpenMediaPreview);
    const onOpenDocumentPreviewRef = useRef(onOpenDocumentPreview);
    const onOpenPdfPreviewRef = useRef(onOpenPdfPreview);
    const extraFileActionsRef = useRef(extraFileActions);
    const destinationPickerRef = useRef<{
      operation: 'Copy' | 'Move';
      items: FileSystemItem[];
    } | null>(null);
    onOpenMediaPreviewRef.current = onOpenMediaPreview;
    onOpenDocumentPreviewRef.current = onOpenDocumentPreview;
    onOpenPdfPreviewRef.current = onOpenPdfPreview;
    extraFileActionsRef.current = extraFileActions;

    const routes = useMemo(() => buildMountRoutes(mounts), [mounts]);
    routesRef.current = routes;
    const provider = useMemo(() => createUnifiedFileBrowserProvider(mounts), [mounts]);
    const currentMountUnavailable = isMountUnavailable(currentMount);
    const healthBannerMount =
      currentMountUnavailable ? currentMount : currentMount == null ? routes.find(isMountUnavailable) ?? null : null;
    const canWrite = currentMount?.mode === 'ReadWrite' && !currentMountUnavailable;
    canWriteRef.current = canWrite;
    allowOfficeEditRef.current = allowOfficeEdit;

    const applyExtraFileToolbar = useCallback(() => {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          const instance = fileManagerRef.current?.instance();
          if (!instance) return;
          const selected = instance.getSelectedItems?.() ?? [];
          const actions = extraActionsForSelection(selected, extraFileActionsRef.current);
          if (actions.length === 0) return;
          const current = (instance.option('toolbar.fileSelectionItems') as Array<{ name?: string }> | undefined) ?? [];
          const names = new Set(actions.map((action) => action.name));
          if (current.some((item) => item?.name && names.has(item.name))) return;
          instance.option('toolbar.fileSelectionItems', [...current, ...extraToolbarItems(actions)]);
        });
      });
    }, []);

    useImperativeHandle(
      ref,
      () => ({
        resyncToolbar: () => {
          scheduleFileSelectionToolbarSync(
            fileManagerRef,
            canWriteRef.current,
            routesRef.current,
            allowOfficeEditRef.current
          );
          applyExtraFileToolbar();
        }
      }),
      [applyExtraFileToolbar]
    );

    useLayoutEffect(() => {
      syncFileSelectionToolbar(fileManagerRef, canWrite, routes, allowOfficeEdit);
      applyExtraFileToolbar();
    }, [allowOfficeEdit, applyExtraFileToolbar, canWrite, routes]);

    const resyncToolbar = useCallback(() => {
      scheduleFileSelectionToolbarSync(
        fileManagerRef,
        canWriteRef.current,
        routesRef.current,
        allowOfficeEditRef.current
      );
      applyExtraFileToolbar();
    }, [applyExtraFileToolbar]);

    const syncCutStyles = useCallback(() => {
      const instance = fileManagerRef.current?.instance();
      applyClipboardCutStyles(instance?.element() ?? null, currentDirectoryPathRef.current);
    }, []);

    const runClipboardPaste = useCallback(
      async (destination?: FileSystemItem) => {
        const instance = fileManagerRef.current?.instance();
        if (!instance) return;
        const dest = destination ?? instance.getCurrentDirectory();
        if (!dest) return;
        setPasteBusy(true);
        try {
          await pasteClipboardIntoDirectory(dest, routesRef.current);
          instance.refresh();
        } catch (error) {
          showToast(error instanceof Error ? error.message : String(error), 'error', 5000);
        } finally {
          setPasteBusy(false);
          resyncToolbar();
          syncCutStyles();
        }
      },
      [resyncToolbar, syncCutStyles]
    );

    useEffect(() => subscribeFileBrowserClipboard(() => {
      setClipboardState(getClipboardState());
      resyncToolbar();
      requestAnimationFrame(syncCutStyles);
    }), [resyncToolbar, syncCutStyles]);

    useEffect(() => {
      const onKeyDown = (e: KeyboardEvent) => {
        if (isClipboardShortcutTarget(e.target)) return;
        const pane = paneRef.current;
        if (!pane?.contains(e.target as Node)) return;
        const key = e.key.toLowerCase();
        const mod = e.ctrlKey || e.metaKey;
        if (mod && key === 'c') {
          e.preventDefault();
          void handleJarvisFileAction(JARVIS_CLIPBOARD_COPY, fileManagerRef, routesRef.current);
          return;
        }
        if (mod && key === 'x') {
          e.preventDefault();
          void handleJarvisFileAction(JARVIS_CUT, fileManagerRef, routesRef.current);
          return;
        }
        if (mod && key === 'v') {
          e.preventDefault();
          void runClipboardPaste();
          return;
        }
        if (e.key === 'Escape' && getClipboardState()) {
          e.preventDefault();
          clearClipboard();
        }
      };
      document.addEventListener('keydown', onKeyDown);
      return () => document.removeEventListener('keydown', onKeyDown);
    }, [runClipboardPaste]);

    const openMediaPreviewState = useCallback((state: MediaPreviewState) => {
      onOpenMediaPreviewRef.current(state);
      resyncToolbar();
    }, [resyncToolbar]);

    const openDocumentPreviewState = useCallback((state: DocumentPreviewState) => {
      onOpenDocumentPreviewRef.current(state);
      resyncToolbar();
    }, [resyncToolbar]);

    const openPdfPreviewState = useCallback((state: PdfPreviewState) => {
      onOpenPdfPreviewRef.current(state);
      resyncToolbar();
    }, [resyncToolbar]);

    const searchController = useMemo(
      () =>
        createFileBrowserSearchController(fileManagerRef, {
          getServerSearchContext: () => {
            const parsed = parseVirtualPath(currentDirectoryPathRef.current, routesRef.current);
            if (!parsed?.mount) return null;
            return {
              mountId: parsed.mount.id,
              relativePath: parsed.relative
            };
          }
        }),
      []
    );

    useEffect(() => {
      viewModeSetupRef.current = false;
      narrowNavClosedRef.current = false;
    }, [provider, isNarrow]);

    useEffect(() => {
      return () => {
        persistFileBrowserGridState(fileManagerRef, catalog);
        if (hasEnteredFolderRef.current) {
          persistNavigatedLocation(catalog, currentDirectoryPathRef.current, true, routesRef.current);
        }
      };
    }, [catalog]);

    const openColumnChooser = useCallback(() => {
      if (!isFileBrowserDetailsView(fileManagerRef)) {
        showToast('Column chooser is only available in details view.', 'info', 2500);
        return;
      }
      setColumnChooserOpen(true);
    }, []);

    const closeColumnChooser = useCallback(() => {
      setColumnChooserOpen(false);
    }, []);

    const closeNewFilePrompt = useCallback(() => {
      setNewFilePromptStarterId(null);
    }, []);

    const closeDestinationPicker = useCallback(() => {
      destinationPickerRef.current = null;
      setDestinationPicker(null);
    }, []);

    const openDestinationPicker = useCallback((operation: 'Copy' | 'Move') => {
      const instance = fileManagerRef.current?.instance();
      if (!instance) return;
      const selected = instance.getSelectedItems?.() ?? [];
      const items = filterTransferableItems(selected, routesRef.current);
      if (items.length === 0) {
        showToast(
          selected.length === 0
            ? 'Select one or more items first.'
            : 'Storage locations cannot be copied or moved.',
          'warning',
          3500
        );
        return;
      }
      setDestinationPicker({ operation, items });
      destinationPickerRef.current = { operation, items };
    }, []);

    const handleDestinationSelect = useCallback(
      (mount: MountRoute, folderPath: string) => {
        const picker = destinationPickerRef.current;
        destinationPickerRef.current = null;
        setDestinationPicker(null);
        if (!picker) return;

        const destination = buildDestinationDirectory(mount, folderPath) as FileSystemItem;
        const names = picker.items.map((item) => item.name);
        const verb = picker.operation === 'Move' ? 'Moved' : 'Copied';

        void (async () => {
          try {
            await performFileTransfers(picker.items, destination, routesRef.current, picker.operation);
            fileManagerRef.current?.instance()?.refresh();
            showToast(`${verb} ${transferSelectionLabel(names)}.`, 'success', 3000);
          } catch (error) {
            fileManagerRef.current?.instance()?.refresh();
            const message =
              error && typeof error === 'object' && 'errorText' in error && typeof error.errorText === 'string'
                ? error.errorText
                : error instanceof Error
                  ? error.message
                  : String(error);
            showToast(message, 'error', 5000);
          }
          resyncToolbar();
        })();
      },
      [resyncToolbar]
    );

    const handleCreateNewFile = useCallback(
      (fileName: string) => {
        const starterId = newFilePromptStarterId;
        if (!starterId) return;
        setNewFilePromptStarterId(null);
        void createAndOpenNewStarterFile({
          starterId,
          fileName,
          fileManagerRef,
          routes: routesRef.current,
          allowOfficeEdit: allowOfficeEditRef.current,
          onPreviewInstead: openDocumentPreviewState,
          navigate
        });
      },
      [navigate, newFilePromptStarterId, openDocumentPreviewState]
    );

    const fileSearchOptions = useMemo(() => buildFileBrowserSearchOptions(searchController), [searchController]);
    const newFileMenuItems = useMemo(() => buildNewFileMenuItems(), []);
    const newFileOptions = useMemo(
      () => ({
        icon: 'doc',
        hint: 'New file',
        elementAttr: { title: 'New file', class: 'jarvis-new-file-btn' },
        stylingMode: 'text' as const,
        showArrowIcon: true,
        displayExpr: 'text',
        keyExpr: 'id',
        dropDownOptions: {
          width: 260,
          wrapperAttr: { class: 'jarvis-new-file-menu' }
        },
        items: newFileMenuItems,
        onItemClick: (e: { itemData?: { id?: string; disabled?: boolean } }) => {
          const id = e.itemData?.id;
          if (!id || e.itemData?.disabled || id.startsWith('category-')) return;
          if (!getNewFileStarter(id)) return;
          setNewFilePromptStarterId(id);
        }
      }),
      [newFileMenuItems]
    );
    const columnChooserOptions = useMemo(
      () => ({
        icon: 'columnchooser',
        hint: 'Choose columns',
        stylingMode: 'text' as const,
        onClick: openColumnChooser
      }),
      [openColumnChooser]
    );

    const handleCurrentDirectoryChanged = useCallback(
      (e: { directory?: { path?: string; key?: string; pathKeys?: string[]; dataItem?: { __path?: string; path?: string; key?: string } } }) => {
        const instance = fileManagerRef.current?.instance();
        const rawPath =
          fileManagerDirectoryPath(e.directory) ||
          fileManagerDirectoryPath(instance?.getCurrentDirectory?.() as { path?: string; key?: string; pathKeys?: string[]; dataItem?: { __path?: string } } | undefined);
        const persisted = persistNavigatedLocation(catalog, rawPath, hasEnteredFolderRef.current, routes);
        hasEnteredFolderRef.current = persisted.hasEnteredFolder;
        const path = persisted.path;
        currentDirectoryPathRef.current = path;
        if (path || persisted.hasEnteredFolder) {
          setFileManagerCurrentPath(toFileManagerCurrentPath(path));
          setFileManagerCurrentPathKeys(toFileManagerCurrentPathKeys(path));
        }
        const parsed = parseVirtualPath(path, routes);
        setCurrentMount(parsed?.mount ?? null);
        setCurrentDirectoryLabel(path ? path.replace(/\//g, ' / ') : 'this folder');
        clearFileBrowserSearch(fileManagerRef, searchController);
        scheduleFileSelectionToolbarSync(
          fileManagerRef,
          canWriteRef.current,
          routes,
          allowOfficeEditRef.current
        );
        scheduleConfigureFileBrowserDetailsGridFromRef(fileManagerRef, catalog);
        requestAnimationFrame(syncCutStyles);
      },
      [catalog, routes, searchController, syncCutStyles]
    );

    const handleItemDeleting = (e: ItemDeletingEvent) => {
      const selectedItems = fileManagerRef.current?.instance().getSelectedItems() ?? [];
      const itemKey = deleteItemKey(e.item);
      const batchItems =
        selectedItems.length > 1 && selectedItems.some((item) => deleteItemKey(item) === itemKey)
          ? selectedItems
          : [e.item];
      const unavailable = batchItems
        .map((item) => parseVirtualPath(item.path || item.name, routes)?.mount)
        .find(isMountUnavailable);
      if (unavailable) {
        (e as unknown as { cancel: boolean }).cancel = true;
        showToast(mountHealthMessage(unavailable) ?? 'This storage location is unavailable.', 'warning', 4000);
        return;
      }
      const batchKey = batchItems
        .map(deleteItemKey)
        .sort((left, right) => left.localeCompare(right))
        .join('\u0000');

      let batch = deleteConfirmationBatchRef.current;
      if (!batch || batch.key !== batchKey) {
        const confirmation = confirmDeleteItems(batchItems, routes);
        batch = {
          key: batchKey,
          remainingItemKeys: new Set(batchItems.map(deleteItemKey)),
          confirmation
        };
        deleteConfirmationBatchRef.current = batch;

        void confirmation
          .then((confirmed) => {
            if (!confirmed && deleteConfirmationBatchRef.current === batch) {
              deleteConfirmationBatchRef.current = null;
            }
          })
          .catch(() => {
            if (deleteConfirmationBatchRef.current === batch) {
              deleteConfirmationBatchRef.current = null;
            }
          });
      }

      batch.remainingItemKeys.delete(itemKey);
      const activeBatch = batch;
      const cancelPromise = activeBatch.confirmation
        .then((confirmed) => !confirmed)
        .catch((deleteError: unknown) => ({
          cancel: true,
          errorText: deleteError instanceof Error ? deleteError.message : String(deleteError)
        }));
      if (activeBatch.remainingItemKeys.size === 0) {
        void cancelPromise.finally(() => {
          if (deleteConfirmationBatchRef.current === activeBatch) {
            deleteConfirmationBatchRef.current = null;
          }
        });
      }
      (e as unknown as { cancel: typeof cancelPromise }).cancel = cancelPromise;
    };

    const onContextMenuShowing = useCallback((e: ContextMenuShowingEvent) => {
      handleContextMenuShowing(e, canWriteRef.current, fileManagerRef, routesRef.current, allowOfficeEditRef.current);
      const selected = e.fileSystemItem ? [e.fileSystemItem] : [];
      const actions = extraActionsForSelection(selected, extraFileActionsRef.current);
      if (actions.length === 0) return;
      const fm = fileManagerRef.current?.instance();
      const current = (fm?.option('contextMenu.items') as unknown[]) ?? [];
      fm?.option('contextMenu.items', [...current, ...extraContextMenuItems(actions)]);
    }, []);

    const onContextMenuItemClick = useCallback(
      (e: ContextMenuItemClickEvent) => {
        const name = e.itemData?.name;
        const selected = fileManagerRef.current?.instance().getSelectedItems() ?? [];
        if (invokeExtraFileAction(name, selected, routes, extraFileActionsRef.current)) {
          return;
        }
        if (name === JARVIS_ZIP) {
          void runZipSelection(fileManagerRef, routes, canWriteRef.current);
          return;
        }
        if (name === JARVIS_COPY_TO) {
          openDestinationPicker('Copy');
          return;
        }
        if (name === JARVIS_MOVE_TO) {
          openDestinationPicker('Move');
          return;
        }
        if (name === JARVIS_PASTE) {
          const destination = e.fileSystemItem?.isDirectory ? e.fileSystemItem : undefined;
          void runClipboardPaste(destination);
          return;
        }
        if (name === JARVIS_CUT || name === JARVIS_CLIPBOARD_COPY || name === JARVIS_SHARE) {
          void handleJarvisFileAction(name, fileManagerRef, routes);
          return;
        }
        handleContextMenuItemClick(
          e,
          routes,
          openMediaPreviewState,
          openDocumentPreviewState,
          openPdfPreviewState,
          allowOfficeEditRef.current,
          navigate
        );
      },
      [navigate, openDestinationPicker, openDocumentPreviewState, openMediaPreviewState, openPdfPreviewState, routes, runClipboardPaste]
    );

    const onSelectedFileOpened = useCallback(
      (e: SelectedFileOpenedEvent) => {
        const dir = fileManagerRef.current?.instance()?.getCurrentDirectory();
        const persisted = persistNavigatedLocation(
          catalog,
          fileManagerDirectoryPath(dir as { path?: string; key?: string; pathKeys?: string[]; dataItem?: { __path?: string } } | undefined),
          hasEnteredFolderRef.current,
          routes
        );
        hasEnteredFolderRef.current = persisted.hasEnteredFolder;
        if (persisted.path) currentDirectoryPathRef.current = persisted.path;
        handleSelectedFileOpened(
          e,
          routes,
          openMediaPreviewState,
          fileManagerRef,
          navigate,
          allowOfficeEditRef.current
        );
      },
      [catalog, navigate, openMediaPreviewState, routes]
    );

    const onToolbarItemClick = useCallback(
      (e: ToolbarItemClickEvent) => {
        const name = e.itemData?.name;
        const selected = fileManagerRef.current?.instance().getSelectedItems() ?? [];
        if (invokeExtraFileAction(name, selected, routes, extraFileActionsRef.current)) {
          return;
        }
        if (
          name === JARVIS_ZIP ||
          name === JARVIS_COPY_TO ||
          name === JARVIS_MOVE_TO ||
          name === JARVIS_CUT ||
          name === JARVIS_CLIPBOARD_COPY ||
          name === JARVIS_PASTE ||
          name === JARVIS_SHARE ||
          name === JARVIS_COLUMN_CHOOSER
        ) {
          if (name === JARVIS_COLUMN_CHOOSER) {
            openColumnChooser();
            return;
          }
          if (name === JARVIS_COPY_TO) {
            openDestinationPicker('Copy');
            return;
          }
          if (name === JARVIS_MOVE_TO) {
            openDestinationPicker('Move');
            return;
          }
          if (name === JARVIS_PASTE) {
            void runClipboardPaste();
            return;
          }
          void handleJarvisFileAction(name, fileManagerRef, routes);
          return;
        }

        const docActions = [DOC_TOOLBAR_PREVIEW, DOC_TOOLBAR_EDIT_EXCEL, DOC_TOOLBAR_EDIT_WORD];
        const mediaActions = [MEDIA_TOOLBAR_PREVIEW, MEDIA_TOOLBAR_OPEN];
        const pdfActions = [PDF_TOOLBAR_PREVIEW];
        if (
          !name ||
          (!mediaActions.includes(name) &&
            !pdfActions.includes(name) &&
            !docActions.includes(name) &&
            name !== MICROSOFT_365_OPEN)
        )
          return;
        const item = fileManagerRef.current?.instance().getSelectedItems()?.[0];
        if (!item || item.isDirectory) return;

        let handled = false;
        if (name === MICROSOFT_365_OPEN) {
          void openInMicrosoft365(item, routes).then(resyncToolbar);
          return;
        } else if (name === MEDIA_TOOLBAR_PREVIEW) {
          handled = openMediaPreview(item, routes, openMediaPreviewState, { navigate });
        } else if (name === MEDIA_TOOLBAR_OPEN) {
          handled = openSelectedMediaViewerTab(item, routes, navigate);
        } else if (name === PDF_TOOLBAR_PREVIEW) {
          handled = openPdfPreview(item, routes, openPdfPreviewState, navigate);
        } else if (name === DOC_TOOLBAR_PREVIEW) {
          handled = openDocumentPreview(item, routes, openDocumentPreviewState);
        } else if (name === DOC_TOOLBAR_EDIT_EXCEL) {
          handled = openDocumentEditorTab(item, routes, 'spreadsheet', {
            allowOfficeEdit: allowOfficeEditRef.current,
            onPreviewInstead: openDocumentPreviewState,
            navigate
          });
        } else if (name === DOC_TOOLBAR_EDIT_WORD) {
          handled = openDocumentEditorTab(item, routes, 'richedit', {
            allowOfficeEdit: allowOfficeEditRef.current,
            onPreviewInstead: openDocumentPreviewState,
            navigate
          });
        }

        if (!handled) {
          showToast('This file type is not supported by that action.', 'warning', 3000);
          return;
        }

        resyncToolbar();
      },
      [navigate, openColumnChooser, openDestinationPicker, openDocumentPreviewState, openMediaPreviewState, openPdfPreviewState, resyncToolbar, routes, runClipboardPaste]
    );

    const onSelectionChanged = useCallback(() => {
      syncFileSelectionToolbar(
        fileManagerRef,
        canWriteRef.current,
        routesRef.current,
        allowOfficeEditRef.current
      );
    }, []);

    const onFileManagerReady = useCallback(() => {
      const fileManager = fileManagerRef.current?.instance();
      if (fileManager && !viewModeSetupRef.current) {
        viewModeSetupRef.current = true;
        fileManager.option('itemView.mode', readFileBrowserViewModeForLayout(isNarrow));
        fileManager.on?.('optionChanged', (e: { fullName?: string; value?: unknown }) => {
          if (e.fullName === 'itemView.mode' && isFileBrowserViewMode(e.value)) {
            writeFileBrowserViewMode(e.value);
          }
        });
      }

      if (fileManager && !lastPathRestoredRef.current) {
        lastPathRestoredRef.current = true;
        const saved = resolveLastLocation(catalog, routesRef.current);
        if (saved) {
          const dxPath = toFileManagerCurrentPath(saved);
          const dxKeys = toFileManagerCurrentPathKeys(saved);
          requestAnimationFrame(() => {
            try {
              fileManager.option('currentPathKeys', dxKeys);
              fileManager.option('currentPath', dxPath);
            } catch {
              // Folder may have been removed; stay at catalog root.
            }
          });
        }
      }

      if (isNarrow && fileManager && !narrowNavClosedRef.current) {
        narrowNavClosedRef.current = true;
        requestAnimationFrame(() => closeFileBrowserNavPaneIfOpen(fileManager));
      }

      syncFileSelectionToolbar(
        fileManagerRef,
        canWriteRef.current,
        routesRef.current,
        allowOfficeEditRef.current
      );
      scheduleConfigureFileBrowserDetailsGridFromRef(fileManagerRef, catalog);
      syncFileBrowserSearch(fileManagerRef, searchController);
      requestAnimationFrame(syncCutStyles);
    }, [catalog, isNarrow, searchController, syncCutStyles]);

    const customizeThumbnail = useCallback(
      (item: FileSystemItem) => customizeFileBrowserThumbnail(item, routes),
      [routes]
    );

    const customizeDetailColumns = useCallback(
      (columns: Parameters<typeof customizeFileBrowserDetailColumns>[0]) => {
        let next = customizeFileBrowserDetailColumns(columns);
        if (isPhone) {
          next = next.filter(
            (column) => column.dataField !== 'dateModified' && column.dataField !== 'size'
          );
        }
        return next;
      },
      [isPhone]
    );

    const canPasteHere = Boolean(clipboardState) && canWrite;

    return (
      <div ref={paneRef} className="file-browser-pane">
      {healthBannerMount && (
        <div className="file-browser-health-banner" role="status">
          {mountHealthMessage(healthBannerMount)}
        </div>
      )}
      {clipboardState ? (
        <FileBrowserClipboardBanner
          state={clipboardState}
          canPaste={canPasteHere}
          destinationLabel={currentDirectoryLabel}
          busy={pasteBusy}
          onPaste={() => void runClipboardPaste()}
          onClear={clearClipboard}
        />
      ) : null}
      <FileManager
        ref={fileManagerRef}
        fileSystemProvider={provider}
        currentPath={fileManagerCurrentPath}
        currentPathKeys={fileManagerCurrentPathKeys}
        height="100%"
        upload={UPLOAD_OPTIONS}
        notifications={{ showPanel: true, showPopup: true }}
        customizeThumbnail={customizeThumbnail}
        customizeDetailColumns={customizeDetailColumns}
        onInitialized={onFileManagerReady}
        onContentReady={onFileManagerReady}
        onCurrentDirectoryChanged={handleCurrentDirectoryChanged}
        onItemDeleting={handleItemDeleting}
        onContextMenuShowing={onContextMenuShowing}
        onContextMenuItemClick={onContextMenuItemClick}
        onSelectedFileOpened={onSelectedFileOpened}
        onToolbarItemClick={onToolbarItemClick}
        onSelectionChanged={onSelectionChanged}
      >
        <Permissions
          create={canWrite}
          copy={false}
          move={false}
          delete={canWrite}
          rename={canWrite}
          upload={canWrite}
          download={!currentMountUnavailable}
        />
        <ItemView showParentFolder />
        <Toolbar>
          <ToolbarItem name="showNavPane" />
          <ToolbarItem name="separator" />
          <ToolbarItem name="create" visible={canWrite} disabled={!canWrite} />
          <ToolbarItem name="upload" visible={canWrite} disabled={!canWrite} />
          <ToolbarItem
            name={JARVIS_NEW_FILE}
            visible={canWrite}
            disabled={!canWrite}
            widget="dxDropDownButton"
            options={newFileOptions}
          />
          <ToolbarItem name="refresh" />
          <ToolbarItem name="separator" location="after" />
          <ToolbarItem name="switchView" />
          <ToolbarItem
            name={JARVIS_COLUMN_CHOOSER}
            location="after"
            locateInMenu={isPhone ? 'always' : 'never'}
            widget="dxButton"
            options={columnChooserOptions}
          />
          <ToolbarItem
            name={JARVIS_FILE_SEARCH}
            location="after"
            locateInMenu="never"
            widget="dxTextBox"
            cssClass="jarvis-file-browser-search"
            options={fileSearchOptions}
          />
        </Toolbar>
        <ContextMenu>
          <ContextMenuItem name="download" />
        </ContextMenu>
        <Details>
          <Column dataField="thumbnail" />
          <Column dataField="name" caption="Name" sortOrder="asc" />
          <Column dataField="dateModified" caption="Modified" width={110} dataType="date" />
          <Column dataField="size" caption="Size" width={90} />
        </Details>
      </FileManager>
      <FileBrowserColumnChooserPopup
        visible={columnChooserOpen}
        onClose={closeColumnChooser}
        fileManagerRef={fileManagerRef}
      />
      <FileBrowserNewFilePromptPopup
        visible={newFilePromptStarterId != null}
        starterId={newFilePromptStarterId}
        onClose={closeNewFilePrompt}
        onCreate={handleCreateNewFile}
      />
      <FileBrowserDestinationPickerPopup
        visible={destinationPicker != null}
        operation={destinationPicker?.operation ?? 'Copy'}
        itemNames={(destinationPicker?.items ?? []).map((item) => item.name)}
        mounts={routes}
        initialMountId={
          parseVirtualPath(currentDirectoryPathRef.current, routes)?.mount.id ?? currentMount?.id ?? null
        }
        initialFolderPath={parseVirtualPath(currentDirectoryPathRef.current, routes)?.relative ?? ''}
        onClose={closeDestinationPicker}
        onSelect={handleDestinationSelect}
      />
      </div>
    );
  })
);

export interface FileBrowserProps {
  /** `files` = My Storage only; `spr` = Public + shared mounts; `all` = everything. */
  catalog?: StorageMountCatalog;
  extraFileActions?: FileBrowserExtraFileAction[];
}

export function FileBrowser({ catalog = 'all', extraFileActions }: FileBrowserProps = {}) {
  const { isNarrow, isPhone, canEditOfficeDocuments } = useShellLayout();
  const [mounts, setMounts] = useState<MountSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [mediaPreview, setMediaPreview] = useState<MediaPreviewState | null>(null);
  const [documentPreview, setDocumentPreview] = useState<DocumentPreviewState | null>(null);
  const [pdfPreview, setPdfPreview] = useState<PdfPreviewState | null>(null);
  const fileManagerPaneRef = useRef<FileManagerPaneHandle>(null);

  useEffect(() => {
    setLoading(true);
    listMounts({ catalog })
      .then((items) => setMounts(items))
      .catch((e: unknown) => setError(e instanceof Error ? e.message : String(e)))
      .finally(() => setLoading(false));
  }, [catalog]);

  const closeMediaPreview = useCallback(() => {
    setMediaPreview(null);
    fileManagerPaneRef.current?.resyncToolbar();
  }, []);

  const closeDocumentPreview = useCallback(() => {
    setDocumentPreview(null);
    fileManagerPaneRef.current?.resyncToolbar();
  }, []);

  const closePdfPreview = useCallback(() => {
    setPdfPreview(null);
    fileManagerPaneRef.current?.resyncToolbar();
  }, []);

  if (loading) {
    return (
      <div className="file-browser-status">
        <LoadIndicator visible height={20} width={20} />
        <span>Loading storage shares...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="file-browser-status file-browser-status--error">
        Failed to load storage shares: {error}
      </div>
    );
  }

  if (mounts.length === 0) {
    return (
      <div className="file-browser-status file-browser-status--muted">
        You don't have access to any storage shares.
      </div>
    );
  }

  return (
    <div
      className={`file-browser-host${isNarrow ? ' file-browser-host--compact-nav' : ''}${isPhone ? ' file-browser-host--phone' : ''}`}
    >
      <FileManagerPane
        ref={fileManagerPaneRef}
        mounts={mounts}
        catalog={catalog}
        isNarrow={isNarrow}
        isPhone={isPhone}
        allowOfficeEdit={canEditOfficeDocuments}
        extraFileActions={extraFileActions}
        onOpenMediaPreview={setMediaPreview}
        onOpenDocumentPreview={setDocumentPreview}
        onOpenPdfPreview={setPdfPreview}
      />
      <MediaPreviewPopup state={mediaPreview} onClose={closeMediaPreview} />
      <DocumentPreviewPopup state={documentPreview} onClose={closeDocumentPreview} />
      <PdfPreviewPopup state={pdfPreview} onClose={closePdfPreview} />
    </div>
  );
}

function deleteItemKey(item: FileSystemItem): string {
  return (item.path || item.name).trim().toLowerCase();
}

async function confirmDeleteItems(
  items: readonly FileSystemItem[],
  routes: readonly MountRoute[]
): Promise<boolean> {
  if (items.length === 1) {
    return confirmSingleDelete(items[0], routes);
  }

  const infos = await Promise.all(items.map((item) => getDeleteInfo(item, routes)));
  return showDeleteConfirmation(buildBatchDeleteConfirmationMessage(infos));
}

async function confirmSingleDelete(
  item: FileSystemItem,
  routes: readonly MountRoute[]
): Promise<boolean> {
  const info = await getDeleteInfo(item, routes);
  const itemName = escapeHtml(info.name || item.name);
  const message = info.isDirectory
    ? info.isEmpty
      ? `Delete folder <strong>"${itemName}"</strong>?`
      : `Folder <strong>"${itemName}"</strong> is not empty and contains ${info.directChildCount} direct item${info.directChildCount === 1 ? '' : 's'}.<br><br>Delete this folder and everything inside it?`
    : `Delete file <strong>"${itemName}"</strong>?`;

  return showDeleteConfirmation(message);
}

async function showDeleteConfirmation(messageHtml: string): Promise<boolean> {
  const dialog = custom({
    title: 'Confirm delete',
    messageHtml,
    buttons: [
      {
        text: 'Yes',
        type: 'danger',
        stylingMode: 'contained',
        onClick: () => true
      },
      {
        text: 'No',
        stylingMode: 'outlined',
        onClick: () => false
      },
      {
        text: 'Cancel',
        stylingMode: 'text',
        onClick: () => false
      }
    ]
  });

  try {
    return await dialog.show();
  } catch {
    return false;
  }
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

async function handleJarvisFileAction(
  action: string,
  fileManagerRef: { current: FileManagerRef | null },
  routes: readonly MountRoute[]
): Promise<void> {
  const instance = fileManagerRef.current?.instance();
  if (!instance) return;

  if (action === JARVIS_PASTE) {
    const destination = instance.getCurrentDirectory();
    if (!destination) return;
    try {
      await pasteClipboardIntoDirectory(destination, routes);
      instance.refresh();
    } catch (error) {
      showToast(error instanceof Error ? error.message : String(error), 'error', 5000);
    }
    scheduleFileSelectionToolbarSync(fileManagerRef, true, routes);
    return;
  }

  const selected = instance.getSelectedItems?.() ?? [];
  if (action === JARVIS_CUT) {
    if (cutOrCopySelection(selected, routes, 'cut')) {
      scheduleFileSelectionToolbarSync(fileManagerRef, true, routes);
    }
    return;
  }
  if (action === JARVIS_CLIPBOARD_COPY) {
    if (cutOrCopySelection(selected, routes, 'copy')) {
      scheduleFileSelectionToolbarSync(fileManagerRef, true, routes);
    }
    return;
  }
  if (action === JARVIS_SHARE) {
    const item = selected[0];
    if (item) await shareFileBrowserItem(item, routes);
    return;
  }
  if (action === JARVIS_ZIP) {
    await runZipSelection(fileManagerRef, routes, true);
  }
}

async function runZipSelection(
  fileManagerRef: { current: FileManagerRef | null },
  routes: readonly MountRoute[],
  canWrite: boolean
): Promise<void> {
  const instance = fileManagerRef.current?.instance();
  if (!instance) return;

  const selected = instance.getSelectedItems?.() ?? [];
  if (selected.length === 0) {
    showToast('Select one or more items to zip.', 'warning', 3000);
    return;
  }

  const currentPath = instance.getCurrentDirectory()?.path ?? '';
  const built = buildZipRequest(selected, routes, currentPath);
  if (!built) {
    showToast('Cannot zip the current selection from this location.', 'warning', 3500);
    return;
  }

  const archiveName = await promptArchiveName(built.request.archiveName);
  if (!archiveName) return;

  try {
    await createZipArchive(built.mountId, { ...built.request, archiveName });
    const normalizedName = normalizeArchiveName(archiveName);
    await focusZipArchiveAfterCreate(instance, currentPath, archiveName, routes);
    showToast(`Created ${normalizedName}`, 'success', 3000);
    scheduleFileSelectionToolbarSync(fileManagerRef, canWrite, routes);
  } catch (error) {
    showToast(error instanceof Error ? error.message : String(error), 'error', 5000);
  }
}

function closeFileBrowserNavPaneIfOpen(fileManager: { element: () => HTMLElement }): void {
  const root = fileManager.element();
  if (!root?.querySelector('.dx-drawer-opened')) return;

  const buttons = root.querySelectorAll(
    '.dx-filemanager-toolbar .dx-button, .dx-filemanager-general-toolbar .dx-button'
  );
  for (const button of buttons) {
    const label = (
      button.getAttribute('aria-label') ??
      button.getAttribute('title') ??
      button.textContent ??
      ''
    ).toLowerCase();
    if (label.includes('navigation') || label.includes('nav pane') || label.includes('folder panel')) {
      (button as HTMLButtonElement).click();
      return;
    }
  }
}
